<template>
  <div>
    <div class="mb-2">
      <h4>
        {{ t('documentDetailView.checkBotDetails') }}
      </h4>
      <span v-if="checkBotConfig"
        >{{ t('checkBotResultsTable.config') }}:
        {{ checkBotConfig?.name }}</span
      >
    </div>
    <table-wrapper
      v-model:page-options="pageOptions"
      v-model:filters="filters"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :items="transformedResults"
      :mode="TableMode.CLIENT_SIDE"
      :table-style="{ 'table-layout': 'fixed', 'word-break': 'break-all' }"
    >
      <template #cell(copyDetailsIcon)="row">
        <template v-if="row.data">
          <font-awesome-icon
            v-if="row.data.details"
            class="copy-icon"
            :icon="['fas', 'copy']"
            @click="handleCopyDetailsClick(row.data)"
          />
        </template>
      </template>
    </table-wrapper>
  </div>
</template>

<script lang="ts" setup>
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import {
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '@/components/TableWrapper';
  import {
    ICheckBotConfigurationDto,
    ICheckBotResult,
    ICheckBotResults,
  } from 'types';

  interface ITransformedCheckBotResult extends ICheckBotResult {
    check: string;
  }

  const { t } = useI18n();
  const { createSuccessToast, createErrorToast } = useToast();

  const props = defineProps<{
    checkBotResults?: ICheckBotResults;
    checkBotConfig?: ICheckBotConfigurationDto | null;
  }>();

  const { pageOptions, filters } = useTableWrapper<ITransformedCheckBotResult>(
    TableMode.CLIENT_SIDE,
    'check',
  );

  const filterConfiguration =
    new TableFiltersBuilder<ITransformedCheckBotResult>({
      translationKey: 'checkBotResultsTable',
    })
      .addInputFilter('check', { width: '280px' })
      .addSelectFilter('state', { width: '100px', translationKey: undefined })
      .addInputFilter('result', { width: '200px' })
      .addInputFilter('details', { width: '200px' })
      .build();

  const tableFields = computed(() =>
    new TableFieldsBuilder<ITransformedCheckBotResult>({
      sortable: true,
      labelTranslationKey: 'checkBotResultsTable',
    })
      .addField('check', { width: '20%' })
      .addField('state', { width: '10%' })
      .addField('result', { width: '20%' })
      .addField('details', { width: '50%', truncation: { maxLength: 200 } })
      .addField('copyDetailsIcon' as any, {
        label: '',
        width: '5%',
        sortable: false,
      })
      .build(),
  );

  const transformedResults = computed(() => {
    if (!props.checkBotResults) {
      return [];
    }

    const results: ITransformedCheckBotResult[] = [];

    for (const checkName in props.checkBotResults.results) {
      results.push({
        check: checkName,
        ...props.checkBotResults.results[checkName],
      });
    }

    return results;
  });

  function handleCopyDetailsClick(results: ITransformedCheckBotResult) {
    if (!results.details) return;
    try {
      navigator.clipboard.writeText(results.details);
      createSuccessToast(t('checkBotResultsTable.detailsCopied'));
    } catch {
      createErrorToast(t('checkBotResultsTable.detailsCopyFailed'));
    }
  }
</script>

<style scoped lang="scss">
  h4 {
    margin-top: 2rem;
    margin-bottom: 0;
  }

  .copy-icon {
    cursor: pointer;
  }
</style>
